import React, { useState } from 'react';
import { motion } from 'framer-motion';
import TopBanner from '../../components/TopBanner';
import { Link } from 'react-router-dom';
import Accordion from 'react-bootstrap/Accordion';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { ChevronDown, ChevronUp } from 'react-bootstrap-icons';

export default function CloudSolutions() {
    const homeimg = '/images/service/cloud.png';
    const server_url = process.env.REACT_APP_SERVER;

    // --- Animation Configuration ---
    const fadeInUp = {
        hidden: { opacity: 0, y: 40 },
        visible: { 
            opacity: 1, 
            y: 0, 
            transition: { duration: 0.6, ease: "easeOut" } 
        }
    };

    // Trigger animation when 20% of the element is visible
    const scrollConfig = { once: true, amount: 0.2 };

    const schemaData = {
        "@context": "https://schema.org",
        "@type": "Service",
        "serviceType": "Cloud Solutions",
        "provider": {
            "@type": "Organization",
            "name": "Lurantech",
            "url": "https://lurantech.com",
            "logo": "https://lurantech.com/images/logo.png",
            "contactPoint": {
                "@type": "ContactPoint",
                "telephone": "+91-7226-999-101",
                "contactType": "Customer Service",
                "areaServed": "IN",
                "availableLanguage": "English"
            },
            "address": {
                "@type": "PostalAddress",
                "streetAddress": "FF-401, 4th Floor, 7 Sky Complex, Laxmipura, Near VUDA Cross Road",
                "addressLocality": "Vadodara",
                "addressRegion": "Gujarat",
                "postalCode": "390021",
                "addressCountry": "IN"
            }
        },
        "description": "Leverage our cloud solutions to enhance scalability, improve security, and streamline operations.",
        "offers": [
            {
                "@type": "Offer",
                "priceCurrency": "INR",
                "price": "Contact for pricing",
                "url": "https://lurantech.com/service/cloud-solutions"
            }
        ]
    };

    const [activeKey, setActiveKey] = useState(null);

    return (
        <>
            <HelmetProvider>
                <Helmet>
                    <meta property="og:title" content="LuranTech | Cloud Solutions | Scalable, Secure, and Efficient Cloud Services" />
                    <meta property="og:description" content="Leverage LuranTech's cloud solutions to enhance scalability, improve security, and streamline operations." />
                    <meta property="og:image" content={`${server_url}${homeimg}`} />
                    <meta property="og:url" content="https://lurantech.com/service/cloud-solutions" />
                    <meta property="og:type" content="website" />
                    <meta name="robots" content="index, follow" />
                    <meta name="description" content="Leverage LuranTech's cloud solutions to enhance scalability, improve security, and streamline operations." />
                    <meta name="keywords" content="Cloud Solutions, Cloud Computing, Cloud Migration, Scalable Infrastructure, Cloud Security" />
                    <link rel="canonical" href="https://lurantech.com/service/cloud-solutions" />
                    <title>LuranTech | Cloud Solutions | Scalable, Secure, and Efficient Cloud Services</title>
                </Helmet>

                <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }} />
                
                <TopBanner title="Cloud Solutions" />

                <div className="it-sol-con container">
                    {/* Introduction */}
                    <motion.p 
                        initial="hidden"
                        whileInView="visible"
                        viewport={scrollConfig}
                        variants={fadeInUp}
                    >
                        At LuranTech, we offer comprehensive <strong>Cloud Solutions</strong> that help businesses scale their operations, improve security, and achieve greater efficiency. Our cloud services are designed to accelerate your digital transformation journey, enabling you to unlock new business opportunities and reduce operational costs. Whether you are migrating to the cloud or optimizing existing cloud infrastructure, we provide custom solutions tailored to your needs.
                    </motion.p>
                    
                    <br />

                    {/* First Row */}
                    <div className='row g-4'>
                        <motion.div 
                            className="col-12 col-md-6"
                            initial="hidden"
                            whileInView="visible"
                            viewport={scrollConfig}
                            variants={fadeInUp}
                        >
                            <section className="service-section">
                                <div className='row g-2 p-2'>
                                    <div className="col-4" style={{textAlign: 'center'}}>
                                        <img src="/images/icons/cloud-services-1.png" alt="Cloud Infra" style={{width: '100px'}} />
                                    </div>
                                    <div className="col-8 d-flex align-items-center justify-content-center">
                                        <h3>Cloud Infrastructure Management</h3>
                                    </div>
                                    <hr />
                                    <Accordion activeKey={activeKey} onSelect={(k) => setActiveKey(k)}>
                                        <Accordion.Item eventKey="0">
                                            <Accordion.Header>
                                                {activeKey === "0" ? <ChevronUp /> : <ChevronDown />}
                                                <span className='truncate-2'>We provide end-to-end cloud infrastructure management services to ensure high availability and scalability.</span>
                                            </Accordion.Header>
                                            <Accordion.Body>
                                                We provide end-to-end cloud infrastructure management services to ensure high availability, scalability, and reliability. Our solutions help you optimize your cloud resources while minimizing operational costs and complexity.
                                            </Accordion.Body>
                                        </Accordion.Item>
                                    </Accordion>
                                    <div className="col-12 mt-2">
                                        <p><strong>Key Benefits:</strong></p>
                                        <ul>
                                            <li><strong>Scalability: </strong>Easily scale your infrastructure based on needs.</li>
                                            <li><strong>Reliability: </strong>Ensure business continuity with redundant systems.</li>
                                            <li><strong>Cost-Efficiency: </strong>Optimize cloud spending via automation.</li>
                                        </ul>
                                    </div>
                                </div>
                            </section>
                        </motion.div>

                        <motion.div 
                            className="col-12 col-md-6"
                            initial="hidden"
                            whileInView="visible"
                            viewport={scrollConfig}
                            variants={fadeInUp}
                        >
                            <section className="service-section">
                                <div className='row g-2 p-2'>
                                    <div className="col-4" style={{textAlign: 'center'}}>
                                        <img src="/images/icons/cloud-services-2.png" alt="Cloud Security" style={{width: '100px'}} />
                                    </div>
                                    <div className="col-8 d-flex align-items-center justify-content-center">
                                        <h3>Cloud Security</h3>
                                    </div>
                                    <hr />
                                    <Accordion activeKey={activeKey} onSelect={(k) => setActiveKey(k)}>
                                        <Accordion.Item eventKey="1">
                                            <Accordion.Header>
                                                {activeKey === "1" ? <ChevronUp /> : <ChevronDown />}
                                                <span className='truncate-2'>Ensure the security of your cloud data with our comprehensive cloud security solutions.</span>
                                            </Accordion.Header>
                                            <Accordion.Body>
                                                Ensure the security of your cloud data with our comprehensive cloud security solutions. From identity management to data encryption, we take a proactive approach to safeguard your cloud environment against emerging threats.
                                            </Accordion.Body>
                                        </Accordion.Item>
                                    </Accordion>
                                    <div className="col-12 mt-2">
                                        <p><strong>Key Benefits:</strong></p>
                                        <ul>
                                            <li><strong>Data Protection: </strong>Robust encryption and access controls.</li>
                                            <li><strong>Threat Detection: </strong>Mitigate risks using advanced monitoring tools.</li>
                                            <li><strong>Compliance: </strong>Ensure compliance with standards like GDPR.</li>
                                        </ul>
                                    </div>
                                </div>
                            </section>
                        </motion.div>
                    </div>

                    <br /><br />

                    {/* Second Row */}
                    <div className='row g-4'>
                        <motion.div 
                            className="col-12 col-md-6"
                            initial="hidden"
                            whileInView="visible"
                            viewport={scrollConfig}
                            variants={fadeInUp}
                        >
                            <section className="service-section">
                                <div className='row g-2 p-2'>
                                    <div className="col-4" style={{textAlign: 'center'}}>
                                        <img src="/images/icons/cloud-services-3.png" alt="Cloud Migration" style={{width: '100px'}} />
                                    </div>
                                    <div className="col-8 d-flex align-items-center justify-content-center">
                                        <h3>Cloud Migration</h3>
                                    </div>
                                    <hr />
                                    <Accordion activeKey={activeKey} onSelect={(k) => setActiveKey(k)}>
                                        <Accordion.Item eventKey="2">
                                            <Accordion.Header>
                                                {activeKey === "2" ? <ChevronUp /> : <ChevronDown />}
                                                <span className='truncate-2'>Seamlessly migrate your applications and data to the cloud with minimal disruption.</span>
                                            </Accordion.Header>
                                            <Accordion.Body>
                                                Seamlessly migrate your applications and data to the cloud with minimal disruption. Our cloud migration services ensure a smooth transition, minimizing risks and optimizing performance in your new cloud environment.
                                            </Accordion.Body>
                                        </Accordion.Item>
                                    </Accordion>
                                    <div className="col-12 mt-2">
                                        <p><strong>Key Benefits:</strong></p>
                                        <ul>
                                            <li><strong>Risk Reduction: </strong>Secure migration with professional support.</li>
                                            <li><strong>Performance: </strong>Enhance scalability post-migration.</li>
                                            <li><strong>Efficiency: </strong>Reduce IT overhead via flexible models.</li>
                                        </ul>
                                    </div>
                                </div>
                            </section>
                        </motion.div>

                        <motion.div 
                            className="col-12 col-md-6"
                            initial="hidden"
                            whileInView="visible"
                            viewport={scrollConfig}
                            variants={fadeInUp}
                        >
                            <section className="service-section">
                                <div className='row g-2 p-2'>
                                    <div className="col-4" style={{textAlign: 'center'}}>
                                        <img src="/images/icons/cloud-services-4.png" alt="Automation" style={{width: '100px'}} />
                                    </div>
                                    <div className="col-8 d-flex align-items-center justify-content-center">
                                        <h3>Cloud Automation</h3>
                                    </div>
                                    <hr />
                                    <Accordion activeKey={activeKey} onSelect={(k) => setActiveKey(k)}>
                                        <Accordion.Item eventKey="3">
                                            <Accordion.Header>
                                                {activeKey === "3" ? <ChevronUp /> : <ChevronDown />}
                                                <span className='truncate-2'>Leverage cloud automation to streamline tasks and enhance deployment cycles.</span>
                                            </Accordion.Header>
                                            <Accordion.Body>
                                                Leverage cloud automation to streamline repetitive tasks, enhance deployment cycles, and improve overall productivity. We help you implement DevOps principles for faster, more reliable cloud operations.
                                            </Accordion.Body>
                                        </Accordion.Item>
                                    </Accordion>
                                    <div className="col-12 mt-2">
                                        <p><strong>Key Benefits:</strong></p>
                                        <ul>
                                            <li><strong>Operational Efficiency: </strong>Automate routine tasks to reduce effort.</li>
                                            <li><strong>Faster Deployment: </strong>Deploy faster with automated processes.</li>
                                            <li><strong>Agile: </strong>Enable CI/CD pipelines for development.</li>
                                        </ul>
                                    </div>
                                </div>
                            </section>
                        </motion.div>
                    </div>

                    <br />

                    {/* Bottom CTA Section */}
                    <motion.div 
                        initial="hidden"
                        whileInView="visible"
                        viewport={scrollConfig}
                        variants={fadeInUp}
                    >
                        <h2>Why Choose LuranTech for Cloud Solutions?</h2>
                        <p>With years of experience and a deep understanding of cloud technologies, LuranTech provides innovative cloud solutions to help businesses scale efficiently and securely. We are committed to helping you achieve your digital transformation goals with tailored solutions that align with your unique business requirements.</p>
                        <ul>
                            <li><strong>Cloud Strategy: </strong>Assess your goals and implement the best cloud solutions.</li>
                            <li><strong>Custom Cloud Solutions: </strong>Tailored architectures for your business.</li>
                            <li><strong>Cloud Support: </strong>Ensuring optimized performance and cost-efficiency.</li>
                        </ul>
                        <hr />
                        <p>Ready to accelerate your digital transformation? <Link style={{fontWeight:'600', color:'#232323', textDecoration:'underline'}} to='/contact-us'>Contact us today</Link>.</p>
                        <span style={{fontSize:'10px'}}>All icons by <a target="_blank" rel="noreferrer" href="https://icons8.com">Icons8</a></span>
                    </motion.div>
                </div>
            </HelmetProvider>
        </>
    );
}